// ============================================================================
// TENANT ADMIN BUSINESS METRICS VIEW
//
// For tenant_admin only:
// - VALUE & ROI metrics ONLY
// - Time Reclaimed (formatted: < 60 min → "X min", ≥ 60 → "Hh Mm")
// - Estimated Time Without Gate
// - Not-Yet → Completed Conversion (case-based, from getConversionMetrics)
// - Breakdown by specialty / pack (framed as value/performance)
// - Date range filter (Last 7/30/90 days)
//
// Trust & Clarity:
// - n/d under every % metric, "Low sample" badge when d < 20, "—" when d === 0
// - MetricsContextStrip with filters, total runs, last updated
// - InfoTooltip on metric labels
//
// USAGE & QUALITY metrics (Completion Rate, Intercept Rate, Urgent Rate,
// Override Rate, Outcome Distribution) live on the Analytics Dashboard.
//
// READS FROM:
// - tenants/{tenantId}/rollups/daily/dates/{dateId}
// - tenants/{tenantId}/metrics (for conversion calculation)
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { safeUserError } from '../core/safeError.js';
import {
  TrendingUp, AlertTriangle, RefreshCw, Clock, Timer,
  Calendar, ArrowRight, AlertCircle, Package, Shield
} from 'lucide-react';
import {
  getPlatformAnalyticsFromRollups,
  getImpactSettings,
  getConversionMetrics,
} from '../core/index.js';
import { useAuth } from '../auth/AuthContext.jsx';
import InfoTooltip from '../components/InfoTooltip.jsx';
import MetricsContextStrip from '../components/MetricsContextStrip.jsx';
import MetricValue from '../components/MetricValue.jsx';

// ============================================================================
// FORMATTING HELPERS
// ============================================================================

const formatReclaimedTime = (minutes) => {
  if (minutes === null || minutes === undefined || minutes === 0) {
    return null;
  }
  if (minutes < 60) {
    return `${Math.round(minutes)} min`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMins = Math.round(minutes % 60);
  if (remainingMins === 0) {
    return `${hours}h`;
  }
  return `${hours}h ${remainingMins}m`;
};

// Date range presets
const DATE_RANGES = [
  { id: '7d', label: 'Last 7 days', days: 7 },
  { id: '30d', label: 'Last 30 days', days: 30 },
  { id: '90d', label: 'Last 90 days', days: 90 },
];

const formatDateId = (date) => {
  const d = new Date(date);
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDateRange = (daysBack) => {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - daysBack);
  return {
    startDateId: formatDateId(startDate),
    endDateId: formatDateId(endDate),
  };
};

// Tooltip definitions for Business Metrics
const TOOLTIPS = {
  timeReclaimed: {
    label: 'Time Reclaimed',
    definition: 'Estimated specialist time saved by intercepting incomplete referrals before they reach review.',
    formula: 'not-yet count × minutesSavedPerPreventedReferral',
  },
  timeWithoutGate: {
    label: 'Estimated Time Without Gate',
    definition: 'How much specialist time would have been wasted reviewing incomplete referrals without the gate.',
    formula: 'not-yet count × minutesSpentPerIncompleteReferral',
  },
  conversionRate: {
    label: 'Not-Yet Conversion',
    definition: 'Tracks how not-yet referrals progress after the initial gate decision. Conversion means a not-yet referral was later re-evaluated by the engine and reached routine or urgent.',
    formula: 'converted / not-yet cases',
  },
  notYetEngineConversion: {
    label: 'Converted',
    definition: 'Referrals that were initially NOT_YET but later reached routine or urgent after the user completed missing information and re-ran the gate engine.',
    formula: 'engine conversions / not-yet cases',
  },
  overrideRate: {
    label: 'Override Rate',
    definition: 'Percentage of not-yet referrals that were resolved by manual override rather than engine re-evaluation.',
    formula: 'overridden referrals / total not-yet referrals',
  },
  interceptPct: {
    label: 'Intercept %',
    definition: 'Percentage of referrals that were incomplete and intercepted before reaching specialists.',
    formula: 'not-yet / total runs',
  },
};

// KPI Card Component
const KpiCard = ({ icon: Icon, label, value, subtitle, color = 'emerald', isEmpty = false, tooltip, children }) => {
  const colorClasses = {
    emerald: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400',
    amber: 'bg-amber-500/10 border-amber-500/30 text-amber-400',
    red: 'bg-red-500/10 border-red-500/30 text-red-400',
    slate: 'bg-slate-500/10 border-slate-500/30 text-slate-400',
  };

  const iconColorClasses = {
    emerald: 'bg-emerald-500/20 text-emerald-400',
    amber: 'bg-amber-500/20 text-amber-400',
    red: 'bg-red-500/20 text-red-400',
    slate: 'bg-slate-500/20 text-slate-400',
  };

  const emptyClasses = isEmpty ? 'opacity-60' : '';

  return (
    <div className={`rounded-2xl border p-6 ${colorClasses[color] || colorClasses.slate} ${emptyClasses}`}>
      <div className="flex items-start justify-between mb-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${iconColorClasses[color] || iconColorClasses.slate}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
      <p className={`text-3xl font-bold mb-1 ${isEmpty ? 'text-slate-500' : 'text-white'}`}>{value}</p>
      <p className="text-sm font-medium flex items-center">
        {label}
        {tooltip && <InfoTooltip {...tooltip} />}
      </p>
      {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
      {children}
    </div>
  );
};

// Main Component
const TenantAdminBusinessMetrics = () => {
  const { user } = useAuth();
  const [dateRange, setDateRange] = useState('30d');
  const [analytics, setAnalytics] = useState(null);
  const [impactSettings, setImpactSettings] = useState(null);
  const [conversionMetrics, setConversionMetrics] = useState(null);
  const [conversionLoading, setConversionLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const tenantId = user?.tenantId;

  // Load rollup data, impact settings, and conversion metrics
  const loadData = useCallback(async () => {
    if (!tenantId) {
      setLoading(false);
      setError('No tenant assigned to your account');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const rangeConfig = DATE_RANGES.find(r => r.id === dateRange) || DATE_RANGES[1];
      const { startDateId, endDateId } = getDateRange(rangeConfig.days);

      // Load rollup analytics and impact settings in parallel
      const [data, settings] = await Promise.all([
        getPlatformAnalyticsFromRollups({
          tenantId,
          startDateId,
          endDateId,
        }),
        getImpactSettings(tenantId),
      ]);

      setAnalytics(data);
      setImpactSettings(settings);

      // Load case-based conversion metrics (separate, non-blocking)
      setConversionLoading(true);
      try {
        const convData = await getConversionMetrics(tenantId, {});
        setConversionMetrics(convData);
      } catch (convErr) {
        console.warn('[TenantAdminBusinessMetrics] Conversion metrics not available:', convErr);
        setConversionMetrics(null);
      } finally {
        setConversionLoading(false);
      }
    } catch (err) {
      console.error('[TenantAdminBusinessMetrics] Error loading analytics:', err);
      setError(safeUserError(err));
    } finally {
      setLoading(false);
    }
  }, [tenantId, dateRange]);

  useEffect(() => {
    if (tenantId) {
      loadData();
    }
  }, [loadData, tenantId]);

  // Calculate VALUE KPIs from rollup data
  const calculateKpis = () => {
    const meta = analytics?.meta || {};
    const hasData = meta.hasData || false;

    const minutesSpentPerIncomplete = impactSettings?.assumptions?.minutesSpentPerIncompleteReferral || 15;

    if (!analytics?.totals) {
      return {
        hasData: false,
        totalRuns: 0,
        reclaimedMinutes: 0,
        wastedMinutesWithoutGate: 0,
        notYetCount: 0,
        lastUpdatedAt: null,
      };
    }

    const totals = analytics.totals;

    return {
      hasData,
      totalRuns: totals.totalRuns || 0,
      reclaimedMinutes: totals.reclaimedMinutes || 0,
      wastedMinutesWithoutGate: (totals.not_yet || 0) * minutesSpentPerIncomplete,
      notYetCount: totals.not_yet || 0,
      lastUpdatedAt: meta.lastUpdatedAt || null,
    };
  };

  const kpis = calculateKpis();
  const hasData = kpis.hasData;

  // Context strip values
  const dateRangeLabel = DATE_RANGES.find(r => r.id === dateRange)?.label || 'Last 30 days';
  const contextFilters = { dateRange: dateRangeLabel };

  // Error state
  if (error) {
    return (
      <div className="p-4 md:p-8">
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 text-center">
          <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-4" />
          <p className="text-red-400 font-medium mb-2">Unable to load Business Metrics</p>
          <p className="text-sm text-slate-400 mb-4">{error}</p>
          <button
            onClick={loadData}
            className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 rounded-xl text-white font-medium mx-auto"
          >
            <RefreshCw className="w-4 h-4" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-500/10 border border-violet-500/20 mb-4">
            <TrendingUp className="w-4 h-4 text-violet-400" />
            <span className="text-xs font-medium text-violet-400 uppercase">Value & ROI</span>
          </div>
          <h1 className="text-2xl font-semibold text-white mb-2">Business Metrics</h1>
          <p className="text-slate-400">
            Value delivered and return on investment
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-slate-400" />
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="bg-slate-800/50 border border-slate-700/50 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-violet-500/50"
            >
              {DATE_RANGES.map((range) => (
                <option key={range.id} value={range.id}>
                  {range.label}
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={loadData}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Loading State */}
      {loading && !analytics && (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 text-violet-400 animate-spin mx-auto mb-4" />
            <p className="text-slate-400">Loading business metrics...</p>
          </div>
        </div>
      )}

      {/* KPI Cards */}
      {analytics && (
        <>
          {/* Context Strip */}
          <MetricsContextStrip
            filters={contextFilters}
            totalRuns={kpis.totalRuns}
            lastUpdatedAt={kpis.lastUpdatedAt}
          />

          {/* Value KPIs - Time Impact */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
            <KpiCard
              icon={Timer}
              label="Time Reclaimed"
              value={formatReclaimedTime(kpis.reclaimedMinutes) || '—'}
              subtitle={hasData ? `From ${kpis.notYetCount} incomplete referrals prevented` : 'No data for this period'}
              color="emerald"
              isEmpty={!hasData}
              tooltip={TOOLTIPS.timeReclaimed}
            />
            <KpiCard
              icon={AlertCircle}
              label="Estimated Time Without Gate"
              value={formatReclaimedTime(kpis.wastedMinutesWithoutGate) || '—'}
              subtitle={hasData ? 'Specialist time if incompletes bypassed gate' : 'No data for this period'}
              color="red"
              isEmpty={!hasData}
              tooltip={TOOLTIPS.timeWithoutGate}
            />
          </div>

          {/* Not-Yet Resolution — Split into Conversion + Override Rate */}
          {conversionLoading ? (
            <div className="bg-slate-800/30 border border-slate-700/50 rounded-2xl p-6 mb-8">
              <div className="flex items-center gap-2 py-4">
                <RefreshCw className="w-4 h-4 text-slate-400 animate-spin" />
                <p className="text-sm text-slate-400">Loading resolution data...</p>
              </div>
            </div>
          ) : conversionMetrics ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* ── SPLIT 1: Conversion Funnel ── */}
              <div className="bg-slate-800/30 border border-slate-700/50 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                    <ArrowRight className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white flex items-center">
                      Not-Yet Conversion
                      <InfoTooltip {...TOOLTIPS.conversionRate} />
                    </h3>
                    <p className="text-xs text-slate-500">Not-yet referrals re-evaluated to routine / urgent</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4 mb-5">
                  <div>
                    <p className="text-3xl font-bold text-amber-400">
                      {conversionMetrics.notYetResolution?.notYetCases || conversionMetrics.notYetCases || 0}
                    </p>
                    <p className="text-sm text-slate-400">Not-Yet Cases</p>
                    <p className="text-xs text-slate-600 mt-1">Ever marked not-yet</p>
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-emerald-400">
                      {conversionMetrics.notYetResolution?.notYetConvertedEngineCount ?? conversionMetrics.convertedCases ?? 0}
                    </p>
                    <p className="text-sm text-slate-400 flex items-center gap-1">
                      Converted
                      <InfoTooltip {...TOOLTIPS.notYetEngineConversion} />
                    </p>
                    <p className="text-xs text-slate-600 mt-1">Completed via engine</p>
                  </div>
                  <div>
                    <MetricValue
                      numerator={conversionMetrics.notYetResolution?.notYetConvertedEngineCount ?? conversionMetrics.convertedCases ?? 0}
                      denominator={conversionMetrics.notYetResolution?.notYetCases || conversionMetrics.notYetCases || 0}
                      precision={1}
                      valueClassName="text-3xl font-bold text-white"
                    />
                    <p className="text-sm text-slate-400 mt-1">Conversion Rate</p>
                    <p className="text-xs text-slate-600 mt-1">Not-yet → completed</p>
                  </div>
                </div>
                {/* Mini progress bar — inbox cases only (converted via engine + overridden from inbox + pending) */}
                {(() => {
                  const total = conversionMetrics.notYetResolution?.notYetCases || conversionMetrics.notYetCases || 0;
                  const converted = conversionMetrics.notYetResolution?.notYetConvertedEngineCount ?? conversionMetrics.convertedCases ?? 0;
                  const pending = conversionMetrics.notYetResolution?.notYetStillPendingCount || 0;
                  const overridden = conversionMetrics.notYetResolution?.notYetInboxOverridesCount || 0;
                  if (total === 0) return null;
                  return (
                    <div>
                      <div className="h-2.5 rounded-full bg-slate-700/60 overflow-hidden flex">
                        {converted > 0 && <div className="h-full bg-emerald-500 transition-all" style={{ width: `${(converted / total) * 100}%` }} title={`Converted: ${converted}`} />}
                        {overridden > 0 && <div className="h-full bg-orange-500 transition-all" style={{ width: `${(overridden / total) * 100}%` }} title={`Overridden: ${overridden}`} />}
                        {pending > 0 && <div className="h-full bg-slate-600 transition-all" style={{ width: `${(pending / total) * 100}%` }} title={`Pending: ${pending}`} />}
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" /> Converted {converted}</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-500 inline-block" /> Overridden {overridden}</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-slate-600 inline-block" /> Pending {pending}</span>
                      </div>
                    </div>
                  );
                })()}
              </div>
              {/* ── SPLIT 2: Override Rate ── */}
              <div className="bg-slate-800/30 border border-slate-700/50 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-orange-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white flex items-center">
                      Override Rate
                      <InfoTooltip {...TOOLTIPS.overrideRate} />
                    </h3>
                    <p className="text-xs text-slate-500">Not-yet referrals resolved via manual override</p>
                  </div>
                </div>
                <div className="flex items-end gap-6 mb-5">
                  <div>
                    <MetricValue
                      numerator={conversionMetrics.notYetResolution?.notYetOverridesCount || 0}
                      denominator={conversionMetrics.notYetResolution?.totalNotYetReferrals || conversionMetrics.notYetCases || 0}
                      precision={1}
                      valueClassName="text-4xl font-bold text-orange-400"
                    />
                    <p className="text-sm text-slate-400 mt-2">Overridden / Total Not-Yet</p>
                  </div>
                  <div className="text-sm text-slate-500 pb-1">
                    {conversionMetrics.notYetResolution?.notYetOverridesCount || 0} overridden of {conversionMetrics.notYetResolution?.totalNotYetReferrals || conversionMetrics.notYetCases || 0} not-yet referrals
                  </div>
                </div>
              </div>
            </div>
          ) : hasData ? (
            <div className="bg-slate-800/30 border border-slate-700/50 rounded-2xl p-6 mb-8">
              <p className="text-sm text-slate-500">Resolution tracking requires referral data. No not-yet cases found.</p>
            </div>
          ) : null}

          {/* Breakdown by Specialty (value framing) */}
          {analytics.bySpecialty && Object.keys(analytics.bySpecialty).length > 0 && (
            <div className="mt-8 bg-slate-800/30 border border-slate-700/50 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Performance by Specialty</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-sm text-slate-500 border-b border-slate-700/50">
                      <th className="pb-3 pr-4">Specialty</th>
                      <th className="pb-3 pr-4 text-right">Total Runs</th>
                      <th className="pb-3 pr-4 text-right">Not-Yet Intercepted</th>
                      <th className="pb-3 text-right">
                        <span className="inline-flex items-center">
                          Intercept %
                          <InfoTooltip {...TOOLTIPS.interceptPct} />
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(analytics.bySpecialty)
                      .sort((a, b) => (b[1].not_yet || 0) - (a[1].not_yet || 0))
                      .map(([specialtyId, data]) => {
                        const specTotal = data.totalRuns || 0;
                        const notYet = data.not_yet || 0;
                        const interceptRate = specTotal > 0 ? ((notYet / specTotal) * 100) : 0;
                        return (
                          <tr key={specialtyId} className="border-b border-slate-700/30">
                            <td className="py-3 pr-4 text-white font-medium">{specialtyId}</td>
                            <td className="py-3 pr-4 text-slate-300 text-right">{specTotal}</td>
                            <td className="py-3 pr-4 text-amber-400 text-right">{notYet}</td>
                            <td className="py-3 text-right">
                              <span className="text-amber-400">{interceptRate.toFixed(1)}%</span>
                              <span className="text-xs text-slate-600 ml-1.5">{notYet}/{specTotal}</span>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Breakdown by Pack / Pathway (value framing) */}
          {analytics.byPack && Object.keys(analytics.byPack).length > 0 && (
            <div className="mt-8 bg-slate-800/30 border border-slate-700/50 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <Package className="w-5 h-5 text-violet-400" />
                <h3 className="text-lg font-semibold text-white">Performance by Pathway</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-sm text-slate-500 border-b border-slate-700/50">
                      <th className="pb-3 pr-4">Pathway</th>
                      <th className="pb-3 pr-4 text-right">Total Runs</th>
                      <th className="pb-3 pr-4 text-right">Not-Yet Intercepted</th>
                      <th className="pb-3 text-right">
                        <span className="inline-flex items-center">
                          Intercept %
                          <InfoTooltip {...TOOLTIPS.interceptPct} />
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(analytics.byPack)
                      .sort((a, b) => (b[1].not_yet || 0) - (a[1].not_yet || 0))
                      .map(([packId, data]) => {
                        const packTotal = data.totalRuns || 0;
                        const notYet = data.not_yet || 0;
                        const interceptRate = packTotal > 0 ? ((notYet / packTotal) * 100) : 0;
                        return (
                          <tr key={packId} className="border-b border-slate-700/30">
                            <td className="py-3 pr-4 text-white font-medium">{packId}</td>
                            <td className="py-3 pr-4 text-slate-300 text-right">{packTotal}</td>
                            <td className="py-3 pr-4 text-amber-400 text-right">{notYet}</td>
                            <td className="py-3 text-right">
                              <span className="text-amber-400">{interceptRate.toFixed(1)}%</span>
                              <span className="text-xs text-slate-600 ml-1.5">{notYet}/{packTotal}</span>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* No Data State */}
          {analytics && !hasData && (
            <div className="mt-8 bg-slate-800/30 border border-slate-700/50 rounded-2xl p-8 text-center">
              <Clock className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400 mb-2">No data for this period</p>
              <p className="text-sm text-slate-500">
                Try a different date range.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TenantAdminBusinessMetrics;
